// Reunderwriting Module (Tier 3) PR 3 — combined outcome panel. Reads the
// per-case outcome (computed from the per-lane LaneAssessments — Pass only
// if all non-NA lanes pass) and surfaces a Finalize button. Mirrors the
// Deal Flow OverviewPanels/FinalOutcomePanel.

import { Badge, Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  useFinalizeReunderwritingOutcome,
  useReunderwritingAssessments,
  useReunderwritingOutcome,
} from "@/hooks/reunderwriting/useReunderwriting";

interface Props {
  caseId: string;
  /** When false, hides the Finalize button (still shows the computed
   *  outcome / per-lane breakdown for read-only viewers). */
  canFinalize?: boolean;
}

export function ReunderwritingOutcomePanel({ caseId, canFinalize = false }: Props) {
  const outcome = useReunderwritingOutcome(caseId);
  const assessments = useReunderwritingAssessments(caseId);
  const finalize = useFinalizeReunderwritingOutcome(caseId);
  const toast = useToast();
  const { dateTime } = useDateFormatter();

  if (outcome.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Computing outcome…</p>
      </Card>
    );
  }

  const o = outcome.data;
  const lanes = assessments.data ?? [];

  async function onFinalize() {
    try {
      await finalize.mutateAsync();
      toast.success("Outcome finalized. Closing tab is now unlocked.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <Card>
      <div className="flex flex-col gap-space-3" data-testid="reunderwriting-outcome-panel">
        <header className="flex items-center justify-between gap-space-3 flex-wrap">
          <h3 className="font-display text-h3 m-0">Combined Outcome</h3>
          {o && (
            <Badge
              status={
                o.overallOutcome === "Pass"
                  ? "finalized"
                  : o.overallOutcome === "Fail"
                  ? "error"
                  : "archived"
              }
            >
              {o.isFinalized ? "Finalized · " : "Provisional · "}
              {o.overallOutcome ?? "—"}
            </Badge>
          )}
        </header>

        {o && (
          <p className="text-small m-0">
            Combined DQR: <strong>{o.combinedDqr}</strong>
            {o.declinedReasonSummary && (
              <span className="text-status-error">
                {" "}· {o.declinedReasonSummary}
              </span>
            )}
          </p>
        )}

        <div>
          <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0 mb-space-1">
            Lane assessments
          </h4>
          {lanes.length === 0 ? (
            <p className="text-caption text-gray-mid italic m-0">
              No lane assessments issued yet.
            </p>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
              {lanes.map((la) => (
                <li
                  key={la.id}
                  className="flex items-center gap-space-3 text-small"
                  data-testid={`lane-summary-${la.lane}`}
                >
                  <span className="font-mono">{la.lane}</span>
                  <Badge status={la.outcomePassFail === "Pass" ? "finalized" : "error"}>
                    {la.outcomePassFail}
                  </Badge>
                  <span className="text-gray-mid">DQR {la.dealQualityRating}</span>
                  {la.failureReason && (
                    <span className="text-caption text-status-error truncate">
                      · {la.failureReason}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canFinalize && o && !o.isFinalized && (
          <div className="flex items-center justify-end">
            <Button onClick={onFinalize} disabled={finalize.isPending}>
              Finalize outcome
            </Button>
          </div>
        )}
        {o?.isFinalized && (
          <p className="text-caption text-gray-mid m-0">
            Finalized at {o.finalizedAt ? dateTime(o.finalizedAt) : "—"}
            {o.finalizedByUserId ? ` by ${o.finalizedByUserId}` : ""}.
          </p>
        )}
      </div>
    </Card>
  );
}
